"use client";

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
// Só tipos aqui: `import type` some na compilação, então nenhum dos três SDKs
// entra no bundle inicial. Os valores chegam por `import()` dentro do efeito
// (veja o comentário no `useEffect`). Eram dois chunks, 860KB em disco,
// baixados em toda página — inclusive na home pública, que não usa flag alguma
// antes de hidratar.
import type {
  LDClient,
  LDContext,
  LDSingleKindContext,
} from "@launchdarkly/js-client-sdk";

import { hasTrackingConsent } from "@/lib/analytics/tracking-consent";
import { onFirstInteraction } from "@/lib/on-first-interaction";
import { isPublicPath } from "@/lib/routing/public-paths";

/**
 * Label shown for sessions/traces in LaunchDarkly Observability (replaces raw context keys).
 */
const resolveObservabilitySessionLabel = (context: LDContext): string | undefined => {
  if (typeof context !== "object" || context === null || !("kind" in context)) {
    return undefined;
  }
  if (context.kind !== "user") {
    return undefined;
  }
  const user = context as LDSingleKindContext & {
    email?: string;
    name?: string;
    anonymous?: boolean;
  };
  const key = typeof user.key === "string" ? user.key : String(user.key ?? "");
  if (user.anonymous === true || key === "guest" || key === "anonymous") {
    return "convidado";
  }
  const email = typeof user.email === "string" ? user.email.trim() : "";
  const name = typeof user.name === "string" ? user.name.trim() : "";
  if (email && name) {
    return `${email} (${name})`;
  }
  if (email) {
    return email;
  }
  if (name) {
    return name;
  }
  return key || undefined;
};

/**
 * Contexto de quem não está logado. Com a medição liberada, a chave sai vazia
 * e o SDK gera uma por navegador (guardada no localStorage dele): cada
 * visitante vira uma sessão distinguível e as visitas repetidas da mesma pessoa
 * se agrupam. Com a recusa, todos dividem a chave fixa `guest` — sem
 * identificador persistente para quem disse não.
 */
export const buildGuestContext = (): LDContext => ({
  kind: "user",
  key: hasTrackingConsent() ? "" : "guest",
  anonymous: true,
  name: "convidado",
});

type LaunchDarklyObservabilityContextValue = {
  client: LDClient | null;
  identify: (context: LDContext) => Promise<void>;
};

export const LaunchDarklyObservabilityContext =
  createContext<LaunchDarklyObservabilityContextValue>({
    client: null,
    identify: async () => {},
  });

type LaunchDarklyObservabilityProviderProps = {
  clientSideId: string;
  serviceName: string;
  version: string;
  children: ReactNode;
};

const resolveLdEnvironment = (): string => {
  const fromEnv = process.env.NEXT_PUBLIC_LAUNCHDARKLY_OBSERVABILITY_ENVIRONMENT?.trim();
  if (fromEnv) {
    return fromEnv;
  }
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === "preview") {
    return "preview";
  }
  if (process.env.NODE_ENV !== "production") {
    return "development";
  }
  return "production";
};

/**
 * Same-origin + relative URLs, plus optional API hosts (cannot mix `true` inside an array — LD types).
 * Set `NEXT_PUBLIC_LD_TRACING_ORIGINS=localhost:3001,api.seudominio.com` for direct browser→API fetch.
 */
const buildTracingOrigins = (): boolean | (string | RegExp)[] => {
  const raw = process.env.NEXT_PUBLIC_LD_TRACING_ORIGINS?.trim();
  const extras = raw
    ? raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  if (typeof window === "undefined") {
    return true;
  }
  if (extras.length === 0) {
    return true;
  }
  const escapedOrigin = window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return [new RegExp(`^${escapedOrigin}`), /^\//, ...extras];
};

export function LaunchDarklyObservabilityProvider({
  clientSideId,
  serviceName,
  version,
  children,
}: LaunchDarklyObservabilityProviderProps) {
  const [client, setClient] = useState<LDClient | null>(null);
  const pendingIdentifyRef = useRef<LDContext | null>(null);

  const identify = useCallback(async (context: LDContext) => {
    if (!client) {
      pendingIdentifyRef.current = context;
      return;
    }
    await client.identify(context);
  }, [client]);

  useEffect(() => {
    if (!clientSideId) {
      return;
    }

    let cancelled = false;
    // Preenchido depois que os módulos chegam; o cleanup pode ser chamado antes
    // disso (desmonte rápido), e aí não há nada a desligar.
    let teardown: (() => Promise<void>) | null = null;

    const start = async () => {
      // Os três SDKs entram aqui, fora do caminho crítico. O `Promise.all`
      // mantém o paralelismo que os imports estáticos tinham.
      const [{ createClient }, observabilityMod, sessionReplayMod] =
        await Promise.all([
          import("@launchdarkly/js-client-sdk"),
          import("@launchdarkly/observability"),
          import("@launchdarkly/session-replay"),
        ]);
      if (cancelled) {
        return;
      }

      const Observability = observabilityMod.default;
      const { LDObserve } = observabilityMod;
      const SessionReplay = sessionReplayMod.default;
      const { LDRecord } = sessionReplayMod;

      const environment = resolveLdEnvironment();
      const ldClient = createClient(clientSideId, buildGuestContext(), {
        // Skip /sdk/goals when not using Experimentation; empty responses break response.json().
        fetchGoals: false,
        streaming: true,
        plugins: [
          new Observability({
            // Defer capture until after LD registerPlugins + start() (see LD manualStart docs).
            manualStart: true,
            contextFriendlyName: resolveObservabilitySessionLabel,
            tracingOrigins: buildTracingOrigins(),
            networkRecording: {
              enabled: true,
              recordHeadersAndBody: true,
              urlBlocklist: [],
            },
            serviceName,
            version,
            environment,
            enablePerformanceRecording: true,
          }),
          new SessionReplay({
            manualStart: true,
            contextFriendlyName: resolveObservabilitySessionLabel,
            // default: masks inputs + PII regex; use highlight-mask on sensitive non-input text.
            privacySetting: "default",
            serviceName,
            version,
            environment,
          }),
        ],
      });

      // A partir daqui há o que desligar, mesmo que o `start` abaixo falhe.
      teardown = async () => {
        try {
          await LDRecord.stop();
          await LDObserve.stop();
        } catch {
          // ignore shutdown errors
        }
        await ldClient.close();
      };

      const startResult = await ldClient.start({ timeout: 30 });
      if (cancelled) {
        await ldClient.close();
        return;
      }
      if (startResult.status !== "complete") {
        console.error(
          "[LaunchDarkly] client.start did not complete:",
          startResult.status,
          "error" in startResult ? startResult.error : undefined
        );
      }
      try {
        // Traces e erros seguem sempre: é operação do serviço, não medição de
        // produto. A gravação de sessão, sim, respeita a recusa — é a mesma
        // regra do PostHog, senão o botão "Recusar" promete o que não cumpre.
        await LDObserve.start();
        if (hasTrackingConsent()) {
          await LDRecord.start({ silent: true });
        }
      } catch (err) {
        console.error("[LaunchDarkly] observability/session-replay start failed:", err);
      }
      if (!cancelled) {
        setClient(ldClient);
      }
    };

    // Nas páginas públicas o SDK espera o primeiro gesto do usuário. Medido no
    // stage, gravação de sessão e observability somavam ~500ms de tarefa longa
    // aos 9-11s — fora do caminho crítico, mas ainda dentro da janela que o
    // PageSpeed mede. Nas rotas autenticadas nada muda: quem está logado já
    // interage no primeiro instante, e ali a gravação completa vale mais.
    // Erros de runtime não dependem disto: o `ClientErrorReporter` tem os
    // próprios ouvintes desde o carregamento. E o `identify` feito antes do
    // cliente existir fica enfileirado em `pendingIdentifyRef`.
    let cancelGate: () => void = () => {};
    if (isPublicPath(window.location.pathname)) {
      cancelGate = onFirstInteraction(() => void start());
    } else {
      void start();
    }

    return () => {
      cancelled = true;
      cancelGate();
      void teardown?.();
    };
  }, [clientSideId, serviceName, version]);

  useEffect(() => {
    if (!client) {
      return;
    }
    const pending = pendingIdentifyRef.current;
    if (!pending) {
      return;
    }
    pendingIdentifyRef.current = null;
    void client.identify(pending);
  }, [client]);

  const value = useMemo(
    () => ({
      client,
      identify,
    }),
    [client, identify]
  );

  return (
    <LaunchDarklyObservabilityContext.Provider value={value}>
      {children}
    </LaunchDarklyObservabilityContext.Provider>
  );
}
